import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input, InputGroup, InputGroupText, Spinner } from "reactstrap";
import VirtualKeyboard from "./components/VirtualKeyboard";
import MemberList from "./components/MemberList";
import neoLogo from "../../assets/images/Neo_Logo_A.svg";
import BubbleInactivity from "./components/BubbleInactivity";
import KioskGamificationProfile from "./components/KioskGamificationProfile";
import new1Bg from "../../assets/images/new1Bg.png";
import { useKiosk } from "./hooks/useKiosk";

const KioskPage = () => {
    document.title = "Autoatendimento | NeoApp";
    const navigate = useNavigate();
    
    // Preload da imagem de fundo para evitar piscar quando a página carregar
    const [bgLoaded, setBgLoaded] = useState(false);
    useEffect(() => {
        const img = new Image();
        img.src = new1Bg;
        img.onload = () => setBgLoaded(true);
        img.onerror = () => setBgLoaded(true); // Se falhar, segue em frente
    }, []);

    const {
        tenantLogo,
        tenantLoading,
        members,
        loadingMembers,
        searchTerm,
        isKeyboardVisible,
        selectedMember,
        filteredMembers,
        debouncedSearchTerm,
        actions: {
            handleKeyPress,
            handleDelete,
            handleSpace,
            handleClear,
            handleSelectMember,
            handleBack,
            hideKeyboard,
            toggleKeyboard,
            showKeyboard
        }
    } = useKiosk();

    // Modelo Padrão de Loading Otimizado (igual a outras páginas, aguardando tudo inclusive a Imagem BG)
    if (tenantLoading || loadingMembers || !bgLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", backgroundColor: "#f8f8fb" }}>
                <div className="text-center">
                    <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                    <p className="text-muted mt-3 fw-medium font-size-16">Carregando sistema...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="d-flex flex-column"
            style={{
                minHeight: "100vh",
                position: "relative",
                backgroundColor: "#001D3D", // Fallback color to avoid white flash
                backgroundImage: `url(${new1Bg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}
        >
            {/* Overlay claro e desfocado */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.7)", // Fundo branco translúcido ajustado
                    backdropFilter: "blur(12px)", // Efeito de desfoque unificado
                    WebkitBackdropFilter: "blur(12px)",
                    zIndex: 0
                }}
            ></div>

            {/* Conteúdo Principal (z-index: 1 para ficar acima do overlay) */}
            <div className="d-flex flex-column" style={{ zIndex: 1, position: "relative", minHeight: "100vh" }}>

                {/* Bolhas de inatividade (apenas na tela inicial) */}
                {!selectedMember && <BubbleInactivity members={members} timeout={60000} />}

                {selectedMember ? (
                    <KioskGamificationProfile
                        member={selectedMember}
                        onBack={handleBack}
                    />
                ) : (
                    <>
                        {/* ═══ Header (Topo) ═══ */}
                        <div className="px-4 py-3 position-relative">

                            {/* Botão Superior Direito - Info */}
                            <div className="position-absolute top-0 end-0 p-4 mt-1 me-1" style={{ zIndex: 100 }}>
                                <button
                                    onClick={() => navigate("/kiosk/evaluation")}
                                    className="btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0 border"
                                    style={{ width: "45px", height: "45px" }}
                                    title="Matriz de Avaliações"
                                >
                                    <i className="mdi mdi-information-outline font-size-22 text-secondary"></i>
                                </button>
                            </div>

                            {/* Logo da Empresa no canto superior esquerdo */}
                            <div className="d-flex justify-content-start w-100 mb-2">
                                {tenantLogo && (
                                    <img
                                        src={tenantLogo}
                                        alt="Logo Academia"
                                        style={{ maxHeight: "45px", maxWidth: "160px", objectFit: "contain" }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* ═══ Container Unificado (Busca e Resultados) ═══ */}
                        <div className="flex-grow-1 d-flex justify-content-center align-items-start px-4 mt-4 overflow-hidden mb-4" onClick={hideKeyboard}>
                            <div 
                                className="d-flex flex-column w-100 shadow-lg" 
                                style={{ 
                                    maxWidth: "680px", 
                                    backgroundColor: "rgba(255, 255, 255, 0.8)", 
                                    backdropFilter: "blur(24px)", 
                                    WebkitBackdropFilter: "blur(24px)",
                                    borderRadius: "28px",
                                    border: "1px solid rgba(255, 255, 255, 0.6)",
                                    maxHeight: "100%",
                                    overflow: "hidden",
                                    transition: "all 0.3s ease-in-out"
                                }}
                            >
                                {/* Barra de Busca Interna (Sticky Top) */}
                                <div 
                                    className="px-4 py-3" 
                                    style={{ 
                                        borderBottom: (searchTerm.trim().length > 0 || loadingMembers) ? "1px solid rgba(0, 0, 0, 0.06)" : "none",
                                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                                        transition: "border-color 0.3s ease"
                                    }}
                                >
                                    <InputGroup className="border-0 bg-transparent flex-nowrap align-items-center">
                                        <InputGroupText className="bg-transparent border-0 ps-1 pe-3 py-0">
                                            <i className="mdi mdi-magnify font-size-28" style={{ color: "#8a98ac" }}></i>
                                        </InputGroupText>
                                        <Input
                                            type="text"
                                            placeholder="Toque no teclado ou digite seu nome..."
                                            value={searchTerm}
                                            className="form-control-lg border-0 bg-transparent fw-bold py-2 shadow-none px-0"
                                            style={{ fontSize: "24px", color: "#343a40" }}
                                            readOnly 
                                            onClick={(e) => { e.stopPropagation(); showKeyboard(); }}
                                        />
                                        {searchTerm && (
                                            <InputGroupText
                                                className="bg-transparent border-0 pe-1 py-0"
                                                style={{ cursor: "pointer", zIndex: 10 }}
                                                onClick={(e) => { e.stopPropagation(); handleClear(); }}
                                            >
                                                <i className="mdi mdi-close-circle text-muted font-size-24 hover-opacity-100 transition-all opacity-50"></i>
                                            </InputGroupText>
                                        )}
                                    </InputGroup>
                                </div>

                                {/* Listagem Interna (Área de scroll) - Condicionada a ter buscas */}
                                {((debouncedSearchTerm && debouncedSearchTerm.trim().length >= 2) || loadingMembers) && (
                                    <div className="overflow-auto custom-scrollbar position-relative" style={{ maxHeight: "calc(100vh - 400px)" }}>
                                        <MemberList
                                            members={filteredMembers}
                                            loading={loadingMembers}
                                            onSelect={handleSelectMember}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ═══ Footer / Teclado Virtual (Baixo Fixo) ═══ */}
                        <div className="shadow-lg position-relative" style={{ borderTop: "1px solid rgba(255, 255, 255, 0.3)", zIndex: 10, backgroundColor: "rgba(255, 255, 255, 0.45)", backdropFilter: "blur(15px)", WebkitBackdropFilter: "blur(15px)" }}>

                            {/* Botão Flutuante (Centralizado Pela Borda Top) para minimizar/expandir teclado */}
                            <div
                                className="position-absolute start-50 translate-middle-x"
                                style={{ top: "-18px", zIndex: 12 }}
                            >
                                <button
                                    className="btn btn-light shadow-sm border rounded-pill d-flex align-items-center justify-content-center"
                                    style={{ width: "48px", height: "36px", backgroundColor: "rgba(255, 255, 255, 0.85)", backdropFilter: "blur(5px)" }}
                                    onClick={toggleKeyboard}
                                >
                                    <i className={`mdi ${isKeyboardVisible ? 'mdi-chevron-down' : 'mdi-chevron-up'} font-size-24 text-muted`} style={{ marginTop: "-2px" }}></i>
                                </button>
                            </div>

                            <div className={`mx-auto ${isKeyboardVisible ? 'pb-4 pt-4 px-3' : 'pb-2 pt-3 px-3'}`} style={{ maxWidth: "700px" }}>

                                {/* Renderização Condicional do Teclado */}
                                {isKeyboardVisible && (
                                    <VirtualKeyboard
                                        onKeyPress={handleKeyPress}
                                        onDelete={handleDelete}
                                        onSpace={handleSpace}
                                        onClear={handleClear} // Mesmo que não tenha mais o botão na tela, a func existe //
                                    />
                                )}

                                {/* Logo da Empresa Powered By */}
                                <div
                                    className="d-flex justify-content-end align-items-center mt-3 pb-1 pe-2"
                                    onClick={() => navigate("/dashboard")}
                                    style={{ cursor: "pointer" }}
                                    title="Ir para o Dashboard Interno"
                                >
                                    <span style={{ fontSize: "11px", fontWeight: "800", color: "#b3b3b3", letterSpacing: "1px" }} className="me-2 text-uppercase">POWERED BY</span>
                                    <img src={neoLogo} alt="NeoApp" style={{ height: "14px", filter: "grayscale(100%) opacity(0.5)" }} />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default KioskPage;
